import React, { useEffect, useMemo, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import type { ChatbotQueryRequest, LlmSection } from "../api";
import { type InsightsChatMessage, useInsightsChatbotConfig } from "../context";
import { useInsightsChatbot } from "../hooks";

interface InsightsAssistantProps {
  buildRequest: (question: string) => ChatbotQueryRequest;
  initialPrompt?: string;
}

const renderSections = (sections: LlmSection[]) => {
  return (
    <Stack spacing={2}>
      {sections.map((section, idx) => (
        <Box key={`${section.title}-${idx}`}>
          {section.title && (
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
              {section.title}
            </Typography>
          )}
          {Array.isArray(section.items) && section.items.length > 0 ? (
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {section.items.map((item, itemIdx) => (
                <Box component="li" key={`${idx}-${itemIdx}`} sx={{ mb: 0.5 }}>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : null}
          {section.text && (
            <Typography variant="body2" color="text.secondary">
              {section.text}
            </Typography>
          )}
        </Box>
      ))}
    </Stack>
  );
};

export const InsightsAssistant: React.FC<InsightsAssistantProps> = ({
  buildRequest,
  initialPrompt,
}) => {
  const chatbot = useInsightsChatbot();
  const { draft, setDraft, messages, setMessages } = useInsightsChatbotConfig();
  const endRef = useRef<HTMLDivElement | null>(null);

  const suggestedPrompts = chatbot.data?.data?.suggested_prompts ?? [];
  const followUps = chatbot.data?.data?.follow_up_questions ?? [];

  const canSend = draft.trim().length > 0 && !chatbot.isPending;

  const send = async (question: string) => {
    const trimmed = question.trim();
    if (!trimmed.length) return;

    const userMsg: InsightsChatMessage = {
      id: `${Date.now()}-user`,
      role: "user",
      content: trimmed,
    };

    setMessages((prev) => [...prev, userMsg]);
    setDraft("");

    try {
      const response = await chatbot.mutateAsync(buildRequest(trimmed));
      const assistantMsg: InsightsChatMessage = {
        id: `${Date.now()}-assistant`,
        role: "assistant",
        content: response.data.answer_text || response.data.answer,
        sections:
          Array.isArray(response.data.answer_sections) &&
          response.data.answer_sections.length
            ? response.data.answer_sections
            : undefined,
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch {
      const assistantMsg: InsightsChatMessage = {
        id: `${Date.now()}-assistant-error`,
        role: "assistant",
        content: "Insights assistant is temporarily unavailable. Please try again.",
      };
      setMessages((prev) => [...prev, assistantMsg]);
    }
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length, chatbot.isPending]);

  const quickReplies = useMemo(() => {
    const initial = initialPrompt?.trim() ? [initialPrompt.trim()] : [];
    const items = [...suggestedPrompts, ...followUps];
    const unique = Array.from(new Set([...initial, ...items]));
    return unique.slice(0, 8);
  }, [followUps, initialPrompt, suggestedPrompts]);

  const sendQuickReply = (text: string) => {
    if (chatbot.isPending) return;
    void send(text);
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        height: "100%",
        minHeight: 620,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          p: 2,
          bgcolor: "background.default",
        }}
      >
        <Stack spacing={1.25}>
          {chatbot.isError && (
            <Alert severity="error">
              Insights assistant is temporarily unavailable.
            </Alert>
          )}

          {messages.length === 0 && (
            <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
              <Box
                sx={{
                  maxWidth: "85%",
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 3,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Ask a question to get AI-powered insights for the selected scope and
                  time period.
                </Typography>
              </Box>
            </Box>
          )}

          {messages.map((msg) => {
            const isUser = msg.role === "user";
            return (
              <Box
                key={msg.id}
                sx={{
                  display: "flex",
                  justifyContent: isUser ? "flex-end" : "flex-start",
                }}
              >
                <Box
                  sx={{
                    maxWidth: "85%",
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 3,
                    bgcolor: isUser ? "primary.main" : "background.paper",
                    color: isUser ? "primary.contrastText" : "text.primary",
                    border: isUser ? "none" : "1px solid",
                    borderColor: isUser ? undefined : "divider",
                    boxShadow: isUser ? 1 : 0,
                  }}
                >
                  {"sections" in msg && msg.sections ? (
                    <Box
                      sx={{
                        "& .MuiTypography-root": {
                          color: isUser ? "primary.contrastText" : undefined,
                        },
                      }}
                    >
                      {renderSections(msg.sections)}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {msg.content}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}

          {chatbot.isPending && (
            <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
              <Box
                sx={{
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 3,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Thinking…
                </Typography>
              </Box>
            </Box>
          )}

          <Box ref={endRef} />
        </Stack>
      </Box>

      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          p: 2,
          bgcolor: "background.paper",
        }}
      >
        {quickReplies.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 1.5 }}
          >
            {quickReplies.map((text) => (
              <Button
                key={text}
                size="small"
                variant="outlined"
                disabled={chatbot.isPending}
                onClick={() => sendQuickReply(text)}
                sx={{ textTransform: "none" }}
              >
                {text}
              </Button>
            ))}
          </Stack>
        )}

        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField
            fullWidth
            placeholder="Type a message…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            multiline
            minRows={1}
            maxRows={6}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (canSend) void send(draft);
              }
            }}
          />
          <Button
            variant="contained"
            endIcon={<SendIcon />}
            disabled={!canSend}
            onClick={() => void send(draft)}
            sx={{ height: 40 }}
          >
            Send
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
};
